import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface RouteInfo {
  path: string;
  displayName: string;
  fullPath: string;
  icon?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RouteSearchService implements OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private routesSubject = new BehaviorSubject<RouteInfo[]>([]);
  public routes$: Observable<RouteInfo[]> = this.routesSubject.asObservable();
  private destroy$ = new Subject<void>();

  private routeMappings: { [key: string]: { displayName: string; icon?: string } } = {
    '/home': { displayName: 'HOME', icon: 'heroHome' },
    '/transactions': { displayName: 'TRANSFERS.TRANSFERS', icon: 'heroDocument' },
    '/transactions/new-transfer': { displayName: 'NEWTRANSFER', icon: 'heroPaperAirplane' },
    '/transactions/scheduled-transfers': { displayName: 'SEND_MONEY.SCHEDULED_TRANSFERS.TITLE', icon: 'heroCalendar' },
    '/transactions/new-scheduled-transfer': {
      displayName: 'SEND_MONEY.SCHEDULED_TRANSFERS.NEW_SCHEDULED_TRANSFER',
      icon: 'heroCalendarDays',
    },
    '/transactions/monthly-statements': { displayName: 'STATEMENTS', icon: 'heroDocument' },
    '/transactions/top-up-bank': { displayName: 'NEW_TRANSFER.TOP_ACCOUNT_LABEL', icon: 'heroArrowUp' },
    '/direct-debit': { displayName: 'DIRECT_DEBITS.FORM.TITLE', icon: 'heroCreditCard' },
    '/direct-debit/new': { displayName: 'DIRECT_DEBITS.FORM.NEW', icon: 'heroCreditCard' },
    '/cards': { displayName: 'CARDS', icon: 'heroCreditCard' },
    '/account': { displayName: 'ACCOUNT.ACCOUNT_LABEL', icon: 'heroBuildingLibrary' },
    '/account/user-details': { displayName: 'ACCOUNTSETTINGS', icon: 'heroUser' },
    '/account/address-details': { displayName: 'ADDRESSDETAILS', icon: 'heroHome' },
    '/account/change-email': { displayName: 'CHANGEEMAIL', icon: 'heroAtSymbol' },
    '/account/change-password': { displayName: 'CHANGEPASSWORD', icon: 'heroLockClosed' },
    '/account/change-mobile': { displayName: 'CHANGEMOBILE', icon: 'heroDevicePhoneMobile' },
    '/account/link-accounts': { displayName: 'SOCIAL_LOGIN.LINK_ACCOUNTS', icon: 'heroDocumentCheck' },
    '/account/refer-a-friend': { displayName: 'REFER_A_FRIEND.TITLE', icon: 'heroFaceSmile' },
    '/account/notifications': { displayName: 'NOTIFICATIONS.TITLE', icon: 'heroGlobeAlt' },
    '/account/whats-new': { displayName: 'WHATS_NEW.TITLE', icon: 'heroGlobeAlt' },
  };
  private previousRoute: string = '';

  constructor() {
    this.initializeRoutes();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateRoutesTranslations();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeRoutes(): void {
    const routes: RouteInfo[] = [];

    Object.keys(this.routeMappings).forEach(path => {
      const { displayName, icon } = this.routeMappings[path];
      routes.push({
        path: path,
        displayName: this.translateService.instant(displayName),
        fullPath: path,
        icon: icon,
      });
    });

    this.routesSubject.next(routes);
  }

  private updateRoutesTranslations(): void {
    const updatedRoutes = this.routesSubject.value.map(route => ({
      ...route,
      displayName: this.translateService.instant(this.routeMappings[route.path].displayName),
    }));

    this.routesSubject.next(updatedRoutes);
  }

  public searchRoutes(query: string): RouteInfo[] {
    if (!query || query.trim() === '') {
      return this.getAllRoutes();
    }

    const normalizedQuery = query.toLowerCase().trim();
    return this.routesSubject.value.filter(route => route.displayName.toLowerCase().includes(normalizedQuery));
  }

  public getAllRoutes(): RouteInfo[] {
    return this.routesSubject.value;
  }

  public navigateToRoute(route: RouteInfo): void {
    this.previousRoute = this.router.url;
    this.router.navigate([route.fullPath]);
  }

  public goBack(): void {
    this.router.navigate([this.previousRoute]);
  }
}
